import type {SanitizedAPIError} from '../Logger'

/**
 * A single ignore rule for an expected backend response. A rule matches when
 * the status matches AND every provided discriminator (code/message/path)
 * matches. `code` is the most reliable signal; message/path matching is
 * brittle and only used because the backend exposes no error-code contract yet.
 */
type IgnoreRule = {
  status: number | number[]
  code?: string
  messagePattern?: RegExp
  pathPattern?: RegExp
  reason: string
}

// Expected backend responses that are deliberate product/validation/auth states
// rather than real frontend failures. Each entry is pinned to a cloud-backend
// source line. Only ERR_NOT_ENTITLED is a stable machine code; the rest depend
// on free-form message text and are inherently brittle. A backend-owned
// error-code contract is tracked as a follow-up so these can become code-based.
const IGNORE_RULES: IgnoreRule[] = [
  {
    status: 400,
    code: 'ERR_NOT_ENTITLED',
    pathPattern: /\/policies\/[^/]+\/dlp\/(restrictions|exceptions)$/,
    reason:
      'DLP entitlement denial on restrictions/exceptions routes (dlp_routes.py:292,366). ' +
      'Dopamine file-hash entitlement remains in shouldIgnoreDlpDopamineEntitlement.',
  },
  {
    status: 400,
    messagePattern: /already (exists|assigned)/i,
    reason:
      'Conflicts: Policy/Custom category already exists; User/Group already ' +
      'assigned (policy_management_routes.py:107, custom_category_routes.py:131, ' +
      'assignment_routes.py:165)',
  },
  {
    status: 400,
    messagePattern: /is a reserved policy name$/i,
    reason: 'Reserved policy name (policy_management_routes.py:81)',
  },
  {
    status: 400,
    messagePattern: /^Policy not found$/,
    reason: 'Expected POLICY_NOT_FOUND_ERR_RESPONSE (common.py:36)',
  },
  {
    status: 400,
    messagePattern: /^Invalid value for 'after' query parameter$/,
    reason: 'Pagination cursor validation (common.py:48)',
  },
  {
    status: 404,
    messagePattern: /^Requested resource not found$/,
    pathPattern: /\/neural\//,
    reason:
      'CASB /neural/* file or app not in DB (api-error-responses.md CASB table; ' +
      'api_lambda/common.py:51-54)',
  },
  {
    status: 404,
    messagePattern: /^User does not exist$/,
    pathPattern: /\/get-associated-users$/,
    reason:
      'Endpoints no associated users (api-error-responses.md jetbridge endpoints; ' +
      'get_associated_users.py:80)',
  },
]

/** Returns whether the HTTP status satisfies a rule's single status or status list. */
const isStatusMatch = (rule: IgnoreRule, status?: number): boolean => {
  if (status === undefined) return false
  return Array.isArray(rule.status)
    ? rule.status.includes(status)
    : rule.status === status
}

const REFRESH_TOKEN_PATH = /\/refresh-tokens\/?$/

/**
 * Expected session-expiry failures on POST /refresh-tokens only.
 * Frontend calls this route exclusively via instance.post (axios-config.ts);
 * refresh_tokens lambda returns 400 (invalid/expired token) or 500 (recreation).
 * Timeout/network during refresh has no HTTP status (DOPECONSOLE-6G/6M).
 */
const isRefreshTokenFailure = (error: SanitizedAPIError): boolean => {
  if (!REFRESH_TOKEN_PATH.test(error.requestUrl ?? '')) return false
  if (error.httpMethod !== 'POST') return false
  return error.httpStatus === undefined || error.httpStatus === 400
}

/** Session-expiry messages returned with 401/403 on flightdeck routes (AuthConst). */
const SESSION_AUTH_MESSAGES = new Set(['Forbidden', 'Invalid token.'])

/**
 * Detects session/auth expiry: bare 401/403 or known session-auth messages.
 * These are expected when tokens expire and should not be reported to Sentry.
 */
const isSessionAuthFailure = (error: SanitizedAPIError): boolean => {
  const {httpStatus, backendErrorCode, backendErrorMessage} = error
  if (httpStatus !== 401 && httpStatus !== 403) return false
  if (backendErrorCode) return false
  if (!backendErrorMessage) return true
  return SESSION_AUTH_MESSAGES.has(backendErrorMessage)
}

/**
 * Returns true when an API error is an expected backend product/validation/auth
 * response that should NOT be reported to Sentry.
 *
 * Hard guarantees:
 * - 5xx is never ignored.
 * - Bare 401/403 and session-auth messages ("Forbidden", "Invalid token.")
 *   are ignored (session expiry).
 * - Catalog-backed 404 message+path pairs are ignored; other 404s are captured.
 * - Unknown errors (no status, e.g. network/timeout) are captured, except
 *   POST /refresh-tokens timeout/network during session refresh.
 */
export const shouldIgnoreAPIError = (error: SanitizedAPIError): boolean => {
  const {httpStatus, backendErrorCode, backendErrorMessage, requestUrl} = error

  // Never suppress server-side failures.
  if (httpStatus !== undefined && httpStatus >= 500) return false

  if (isRefreshTokenFailure(error)) return true

  if (isSessionAuthFailure(error)) return true

  return IGNORE_RULES.some(rule => {
    if (!isStatusMatch(rule, httpStatus)) return false
    if (rule.code && backendErrorCode !== rule.code) return false
    if (
      rule.messagePattern &&
      !rule.messagePattern.test(backendErrorMessage ?? '')
    ) {
      return false
    }
    if (rule.pathPattern && !rule.pathPattern.test(requestUrl ?? '')) {
      return false
    }
    return true
  })
}
